import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../services/account/auth.service';
import { GoogleLoginService } from '../../services/google-login/google-login.service';
import { LinkedInLoginService } from '../../services/linkedin-login/linkedin-login.service';
import { environment } from '../../../environments/environment';

import { loginResponse } from '../../models/account/loginResponse';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'app-social-login',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './social-login.component.html',
  styleUrl: './social-login.component.scss'
})
export class SocialLoginComponent {
  private googleLoginService = inject(GoogleLoginService); // Use the custom service
  private linkedinLoginService = inject(LinkedInLoginService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private messageService = inject(MessageService);


  isGoogleLoading = false;
  isLinkedInLoading = false;

  @Input() loginType: 'signIn' | 'signUp' = 'signIn';

  // Triggered by (click) in HTML
  initiateGoogleLogin() {
    this.isGoogleLoading = true;
    this.googleLoginService.login().subscribe({
      next: (accessToken) => {
        // Send Access Token to Backend
        this.processGoogleLogin(accessToken);

      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Authentication Error',
          detail: 'Could not connect to Google. Please try again.'
        });
        this.isGoogleLoading = false;
      }
    });
  }

  initiateLinkedInLogin() {
    this.isLinkedInLoading = true;
    this.linkedinLoginService.login().subscribe({
      next: (code) => {
        // Send Authorization Code to Backend
        this.processLinkedInLogin(code);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Authentication Error',
          detail: err.message || 'Could not connect to LinkedIn. Please try again.'
        });
        this.isLinkedInLoading = false;
      }
    });
  }


  private processGoogleLogin(accessToken: string) {
    this.isGoogleLoading = true;
    this.authService.googleLogin({ accessToken: accessToken }).subscribe({
      next: (response: loginResponse) => {
        this.handleAuthResponse(response);
      },
      error: (error: any) => {
        this.isGoogleLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Login failed on server.'
        });
      }
    });
  }

  private processLinkedInLogin(code: string) {
    this.isLinkedInLoading = true;
    this.authService.linkedinLogin({ code: code, redirectUri: environment.linkedin.redirectUri }).subscribe({
      next: (response: loginResponse) => {
        this.handleAuthResponse(response);
      },
      error: (error: any) => {
        this.isLinkedInLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Login failed on server.'
        });
      }
    });
  }

  private handleAuthResponse(response: loginResponse) {
    this.isGoogleLoading = false;
    this.isLinkedInLoading = false;
    const currentUrl = this.router.url;
    if (response.isNewUser) {
      if (currentUrl.includes('/signup') || currentUrl.includes('/student-signup')) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['/student-signup'], {
            state: {
              userId: response.userId,
              email: response.email, // Ensure backend returns email in response, or fetch it separately
              emailVerified: true
            }
          });
        });
      } else {
        this.router.navigate(['/student-signup'], {
          state: {
            userId: response.userId,
            email: response.email,
            emailVerified: true,
          }
        });
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Account authenticated successfully!'
      });
    } else {
      localStorage.setItem("account", JSON.stringify(response));
      this.router.navigate(['/portal']);
    }
  }
}